import type { ReconciliationActivityRow, ReconciliationResult } from "@/lib/api/payment-reconciliation";

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

export function badgeClass(value: string, result?: ReconciliationResult): string {
  const v = value.toLowerCase();
  if (result === "Matched" || ["paid", "completed", "processed"].includes(v)) return "green";
  if (result === "Mismatch" || ["declined", "failed", "overdue", "missing"].includes(v)) return "red";
  if (result === "Needs Review" || ["pending", "scheduled", "upcoming"].includes(v)) return "yellow";
  if (["refunded", "refund"].includes(v)) return "purple";
  return "gray";
}

export function ComparisonPanel({ row }: { row: ReconciliationActivityRow }) {
  return (
    <div className="pr-comparison-panel">
      <div className="pr-compare-mini school">
        <h4>Our School/Admin Portal</h4>
        <div className="pr-mini-box-grid">
          <div className="pr-mini-box">
            <span>Status</span>
            <strong>{row.school}</strong>
          </div>
          <div className="pr-mini-box">
            <span>Amount</span>
            <strong>{formatCurrency(row.amount)}</strong>
            {row.school === "Overdue" &&
              !row.inLateFeeGracePeriod &&
              row.lateFeeStatus !== "IN_GRACE" &&
              (row.lateFeeAmount ?? 0) > 0 && (
              <div style={{ fontSize: 11, marginTop: 4 }}>
                {row.lateFeeApplied
                  ? `+ ${formatCurrency(row.lateFeeAmount!)} late fee ${
                      row.lateFeeStatus === "PENDING" ? "(pending)" : "applied"
                    }`
                  : `+ ${formatCurrency(row.lateFeeAmount!)} late fee due`}
                <br />
                Total due{" "}
                {formatCurrency(
                  row.amountWithLateFee ?? row.amount + (row.lateFeeAmount ?? 0)
                )}
              </div>
            )}
            {row.school === "Overdue" &&
              (row.inLateFeeGracePeriod || row.lateFeeStatus === "IN_GRACE") && (
              <div style={{ fontSize: 11, marginTop: 4, color: "#64748b" }}>
                Within 3-day grace
                {(row.lateFeeGraceDaysRemaining ?? 0) > 0
                  ? ` (${row.lateFeeGraceDaysRemaining}d left)`
                  : ""}
              </div>
            )}
          </div>
          <div className="pr-mini-box">
            <span>Due Date</span>
            <strong>{row.due}</strong>
          </div>
          <div className="pr-mini-box">
            <span>Invoice</span>
            <strong>{row.invoice}</strong>
          </div>
        </div>
      </div>
      <div className="pr-compare-mini square">
        <h4>Square Dashboard</h4>
        <div className="pr-mini-box-grid">
          <div className="pr-mini-box">
            <span>Status</span>
            <strong>{row.square}</strong>
          </div>
          <div className="pr-mini-box">
            <span>Amount</span>
            <strong>{formatCurrency(row.amount)}</strong>
          </div>
          <div className="pr-mini-box">
            <span>Transaction</span>
            <strong>{row.txn}</strong>
          </div>
          <div className="pr-mini-box">
            <span>Date</span>
            <strong>{row.squareDate}</strong>
          </div>
        </div>
      </div>
      <div className="pr-recommendation">
        <strong>Reason:</strong> {row.reason}
        <br />
        <strong>Recommended action:</strong> {row.action}
      </div>
    </div>
  );
}

export const SCHOOL_STATUS_OPTIONS = [
  "",
  "Completed",
  "Paid",
  "Overdue",
  "Pending",
  "Failed",
  "Refunded",
] as const;

export const SQUARE_STATUS_OPTIONS = [
  "",
  "Paid",
  "Overdue",
  "Declined",
  "Refunded",
  "Missing",
  "Pending",
  "Scheduled",
] as const;

export const RESULT_OPTIONS = ["", "Matched", "Mismatch", "Needs Review"] as const;

export const PAYMENT_TYPE_OPTIONS = [
  "",
  "Full Payment",
  "Payment Plan",
  "Subscription",
  "Refund",
] as const;

export const PROGRAM_OPTIONS = [
  "",
  "EKG Technician Program",
  "Phlebotomy Technician Program",
  "Medical Assistant Program",
  "Surgical Technician Program",
  "Sterile Processing Technician Program",
] as const;
